import h from '../_utils/hyperscript.js';
import scrollTo from '../_utils/scrollTo.js';
export const blockResubmit = (formId, options = {}) => {
  const form = typeof formId === 'string' ? document.getElementById(formId) : formId;
  if (!form) {
    console.warn('block resubmit, element not found with ID: ', formId);
    return;
  }
  options = Object.assign({
    reportValidity: false,
    textUnknownField: 'Unknown field',
    textFeedbackIntro: 'Some fields have been filled in incorrectly. Please check the list below to see which ones and what needs to be fixed.',
    textPlaceholder: 'A correct input is for example "{%placeholder%}".',
    textValueMissing: '{%field%} is required, but not filled in.',
    textTypeMismatch: '{%field%} must be of a valid type.',
    textPatternMismatch: '{%field%} does not match the expected format.',
    textRangeUnderflow: '{%field%} is too low.',
    textRangeOverflow: '{%field%} is too high.',
    textTooShort: '{%field%} is too short.',
    textTooLong: '{%field%} is too long.',
    textStepMismatch: '{%field%} does not match the step size.',
    textBadInput: '{%field%} contains invalid input.',
    textExpectsDate: 'This fields expects a date.',
    textExpectsDatetime: 'This fields expects a date and time.',
    textExpectsEmail: 'This fields expects an e-mail address.',
    textExpectsInteger: 'This fields expects a whole number.',
    textExpectsMonth: 'This fields expects a year and month number.',
    textExpectsNumber: 'This fields expects a number.',
    textExpectsTel: 'This fields expects a telephone number.',
    textExpectsTime: 'This fields expects a time.',
    textExpectsURL: 'This fields expects a link or URL.',
    textExpectsWeek: 'This fields expects a year and week number.'
  }, options);
  const getExpectsText = input => {
    let text;
    switch (input.getAttribute('type')) {
      case 'date':
        text = options.textExpectsDate;
        break;
      case 'datetime-local':
        text = options.textExpectsDatetime;
        break;
      case 'email':
        text = options.textExpectsEmail;
        break;
      case 'month':
        text = options.textExpectsMonth;
        break;
      case 'number':
        if (text.getAttribute('step') === '1') {
          text = options.textExpectsInteger;
        } else {
          text = options.textExpectsNumber;
        }
        break;
      case 'tel':
        text = options.textExpectsTel;
        break;
      case 'time':
        text = options.textExpectsTime;
        break;
      case 'url':
        text = options.textExpectsURL;
        break;
      case 'week':
        text = options.textExpectsWeek;
        break;
    }
    if (text) {
      return ' ' + text;
    }
    return '';
  };
  const getFieldLabel = input => {
    if (input.getAttribute('data-label_on_error')) {
      return input.getAttribute('data-label_on_error');
    }
    const id = input.id;
    if (id) {
      const label = form.querySelector('label[for="' + id + '"]');
      if (label) {
        return label.textContent.trim();
      }
    }
    const fieldset = input.closest('fieldset');
    if (fieldset) {
      const legend = fieldset.querySelector('legend');
      if (legend) {
        return legend.textContent.trim();
      }
    }
    const label = input.closest('label');
    if (label) {
      return label.textContent.trim();
    }
    return input.name || input.placeholder || options.textUnknownField;
  };
  const addInlineFieldAlert = (input, errorMessage) => {
    removeInlineFieldAlert(input);
    let formGroup = input.closest('.form-group');
    if (!formGroup) {
      formGroup = input.closest('legend') || input.parentElement;
      if (!formGroup) {
        return;
      }
    }
    const fieldIdentifier = input.name || input.id;
    const alertId = fieldIdentifier + '-alert-' + Array.from(crypto.getRandomValues(new Uint8Array(8))).map(b => b.toString(16).padStart(2, '0')).join('');
    const alertElement = h('div', {
      class: 'alert alert-danger mt-1 w-100',
      role: 'alert',
      id: alertId,
      'data-field-validation': fieldIdentifier
    }, errorMessage);
    formGroup.appendChild(alertElement);
    const describedBy = input.getAttribute('aria-describedby');
    if (describedBy) {
      const ids = describedBy.split(/\s+/);
      if (!ids.includes(alertId)) {
        ids.push(alertId);
        input.setAttribute('aria-describedby', ids.join(' '));
      }
    } else {
      input.setAttribute('aria-describedby', alertId);
    }
  };
  const removeInlineFieldAlert = input => {
    const fieldIdentifier = input.name || input.id;
    const existingAlert = form.querySelector('[data-field-validation="' + fieldIdentifier + '"]');
    if (!existingAlert) {
      return;
    }
    const alertId = existingAlert.id;
    existingAlert.remove();
    const describedBy = input.getAttribute('aria-describedby');
    if (describedBy) {
      let ids = describedBy.split(/\s+/).filter(id => id && id !== alertId);
      if (ids.length > 0) {
        input.setAttribute('aria-describedby', ids.join(' '));
      } else {
        input.removeAttribute('aria-describedby');
      }
    }
  };
  const inputs = Array.from(form.querySelectorAll('input:not([type="hidden"]),textarea,select'));
  // On blur check field validity and mark invalid if not a required field.
  const setupInput = input => {
    input.addEventListener('blur', () => {
      const validity = input.validity;
      const isInvalid = validity.typeMismatch || validity.patternMismatch || validity.rangeOverflow || validity.rangeUnderflow || validity.stepMismatch || validity.tooLong || validity.tooShort || validity.badInput;
      input.setAttribute('aria-invalid', isInvalid ? 'true' : 'false');
    });
  };
  for (const input of inputs) {
    setupInput(input);
  }

  // Store state.
  let statusAlert = null;
  let canSubmit = true;
  let submitIndex = 0;
  const reportValidity = () => {
    // Remove previous alert on resubmission.
    if (statusAlert) {
      statusAlert.remove();
      statusAlert = null;
    }

    // Remove all inline field alerts
    const existingInlineAlerts = form.querySelectorAll('[data-field-validation]');
    existingInlineAlerts.forEach(alert => alert.remove());
    const errors = [];
    for (const input of inputs) {
      let error = '';
      if (input.required && input.validity.valueMissing) {
        error = options.textValueMissing + getExpectsText(input);
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      } else if (input.validity.typeMismatch) {
        error = options.textTypeMismatch + getExpectsText(input);
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      } else if (input.validity.patternMismatch) {
        error = options.textPatternMismatch + getExpectsText(input);
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      } else if (input.validity.rangeUnderflow) {
        error = options.textRangeUnderflow;
      } else if (input.validity.rangeOverflow) {
        error = options.textRangeOverflow;
      } else if (input.validity.tooLong) {
        error = options.textTooLong;
      } else if (input.validity.tooShort) {
        error = options.textTooShort;
      } else if (input.validity.stepMismatch) {
        error = options.textStepMismatch;
      } else if (input.validity.badInput) {
        error = options.textBadInput + getExpectsText(input);
        if (input.getAttribute('placeholder')) {
          error += ' ' + options.textPlaceholder;
        }
      }
      if (error) {
        error = error.replace('{%field%}', getFieldLabel(input));
        error = error.replace('{%placeholder%}', input.getAttribute('placeholder'));
        error = error.replace('{%max_length%}', input.getAttribute('maxlength'));
        error = error.replace('{%min_length%}', input.getAttribute('minlength'));
        error = error.replace('{%max_value%}', input.getAttribute('max'));
        error = error.replace('{%min_value%}', input.getAttribute('min'));
        error = error.replace('{%step_size%}', input.getAttribute('step'));
        if (errors.indexOf(error) < 0) {
          errors.push(error);
        }
        input.setAttribute('aria-invalid', 'true');
        addInlineFieldAlert(input, error);
      } else {
        input.setAttribute('aria-invalid', 'false');
        removeInlineFieldAlert(input);
      }
    }
    if (errors.length > 0) {
      statusAlert = h('div', {
        'aria-live': 'polite',
        class: 'alert alert-danger',
        role: 'alert'
      }, [h('p', {
        class: 'mb-2'
      }, options.textFeedbackIntro), h('ul', {
        class: 'mb-0'
      }, errors.map(message => h('li', {}, message)))]);
      form.insertAdjacentElement('afterbegin', statusAlert);
      scrollTo(statusAlert);
    }
    return errors.length === 0;
  };
  const handleValidation = () => {
    if (options.reportValidity) {
      reportValidity();
    }
  };
  const buttonSelector = 'button:not([type]),button[type="submit"],input[type="submit"]';
  const buttons = [];
  const addButtons = () => {
    const newButtons = Array.from(form.querySelectorAll(buttonSelector)).filter(button => !buttons.includes(button)
    // Ensure submit buttons are for this form.
    && button.closest('form') === form);
    if (newButtons.length > 0) {
      buttons.push(...newButtons);
      for (const button of newButtons) {
        button.addEventListener('click', handleValidation);
      }
    }
  };
  const updateButtons = () => {
    // Check for new buttons and add them to the submission list whilst your at it.
    addButtons();
    for (const button of buttons) {
      if (canSubmit) {
        button.classList.add('disabled');
        button.setAttribute('disabled', 'true');
      } else {
        button.classList.remove('disabled');
        button.removeAttribute('disabled');
      }
    }
  };
  const releaseButtons = () => {
    canSubmit = true;
    updateButtons();
  };
  form.addEventListener('invalid', releaseButtons);
  const handleSubmission = event => {
    canSubmit = true;
    if (options.reportValidity) {
      canSubmit = reportValidity();
    }
    canSubmit = canSubmit && form.checkValidity();
    if (canSubmit) {
      // Automatically make buttons available in case request takes to long or something went wrong.
      submitIndex++;
      let submitTemp = submitIndex;
      setTimeout(() => {
        if (submitIndex === submitTemp) {
          releaseButtons();
        }
      }, 15e3);
    } else {
      form.reportValidity();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    updateButtons();
  };
  form.addEventListener('submit', handleSubmission);
  addButtons();
};
export default blockResubmit;